import {
  BooleanInput,
  CheckboxGroupInput,
  Create,
  CreateContextProvider,
  DateTimeInput,
  Form,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  Toolbar,
  TopToolbar,
  useCreateController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  Button as MUIButton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Resources } from '../../resource';

import CustomTagInput from 'src/pages/admin/components/CustomTagInput';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useTrandingToken from './useTrandingToken';
import { chains } from 'src/assets/data';

interface FormProps {
  isCreate: boolean;
}
const TrandingTokenForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const redirect = useRedirect();
  const { watch, setValue, trigger } = useFormContext();
  const record = useRecordContext();
  const { findToken } = useTrandingToken();
  const [addressMessage, setAddressMessage] = useState('');
  const chainId = useWatch({ name: 'chainId' });
  const address = useWatch({ name: 'address' });
  const symbol = useWatch({ name: 'symbol' });
  const name = useWatch({ name: 'name' });
  const [noCreate, setNoCreate] = useState<boolean>(true);

  useEffect(() => {
    isCreate && handleOnBlurAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const handleOnBlurAddress = async () => {
    setAddressMessage('');
    if (!address?.trim() || chainId === '' || chainId === undefined) {
      setValue('symbol', '');
      setValue('name', '');
      return;
    }

    try {
      const token = await findToken(address, chainId);
      if (!token) {
        return;
      }
      setValue('symbol', token?.symbol);
      setValue('name', token?.name);
      setNoCreate(true);
    } catch (error: any) {
      setValue('symbol', '');
      setValue('name', '');
      setAddressMessage(t('module.trandingToken.validate.addressNotExists'));
    }
  };

  const hasTypedRef = useRef(false);

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.trandingToken.address')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              source="address"
              label=""
              placeholder={t('module.trandingToken.address')}
              validate={[
                required(),
                () => {
                  if (addressMessage !== '') {
                    return addressMessage;
                  }
                  return undefined;
                },
              ]}
              disabled={!isCreate}
              fullWidth
              helperText={<span style={{ color: 'red' }}>{addressMessage}</span>}
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onChange={() => {
                hasTypedRef.current = true;
              }}
              onBlur={(e) => {
                if (isCreate && hasTypedRef.current) {
                  handleOnBlurAddress();
                  hasTypedRef.current = false;
                }
              }}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.trandingToken.chain')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              label={false}
              disabled={!isCreate}
              source="chainId"
              defaultValue={501424}
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              choices={chains}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.trandingToken.symbol')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={700}>
              {symbol} {symbol && name ? `(${name})` : ''}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.trandingToken.order')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="order"
              validate={[required()]}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type='number'
            />
          </Box>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} spacing={2} mb={1}>
          <Typography
            variant="body2"
            sx={{ flexBasis: '25%', flexShrink: 0 }}
          >
            {t("module.categories.enable")}
          </Typography>
          <Box
            sx={{ flexGrow: 1 }}
          >
            <BooleanInput label='' source="isEnable" helperText={<span style={{ color: 'red' }}>{""}</span>} />
          </Box>

        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
          mt={2}
        >
          <Box sx={{ flexBasis: '25%', flexShrink: 0, mt: -1 }}>
            <Typography variant="body2"> {t('module.trandingToken.start')}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <DateTimeInput
              source="start"
              label=""
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
          mt={2}
        >
          <Box sx={{ flexBasis: '25%', flexShrink: 0, mt: -1 }}>
            <Typography variant="body2"> {t('module.trandingToken.end')}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <DateTimeInput
              source="end"
              label=""
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>


      </Stack>
    </>
  );
};

export default TrandingTokenForm;
